import { apiFetch, readJson } from './client';

export interface DashboardTipAdmin {
  id: string;
  title: string;
  body: string;
  category: string;
  minRole: string;
  sortOrder: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface DashboardTipUpsert {
  title: string;
  body: string;
  category: string;
  minRole: string;
  sortOrder: number;
  isActive: boolean;
}

export interface DashboardTipImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: { index: number; title?: string | null; message: string }[];
}

export const WEBOPS_TIP_MIN_ROLE_OPTIONS = [
  'Everyone',
  'OpsSales',
  'OpsFinance',
  'OpsHRContract',
  'OpsHRCompany',
  'OpsPharmacy',
  'ItSysAdmin'
] as const;

export const WEBOPS_TIP_DEFAULT_MIN_ROLE = 'Everyone';

export const PORTAL_TIP_MIN_ROLE_OPTIONS = [
  'SPR-Admin',
  'SPR-PharmCollab',
  'SPR-Collab',
  'SPR-User'
] as const;

export const PORTAL_TIP_DEFAULT_MIN_ROLE = 'SPR-User';

export const HUB_DASHBOARD_TIPS_API_BASE = '/api/admin/dashboard-tips';
export const CLIENT_PORTAL_DASHBOARD_TIPS_API_BASE = '/api/admin/client-portal/dashboard-tips';

export function createDashboardTipsAdminApi(base: string) {
  return {
    list: async (): Promise<DashboardTipAdmin[]> => {
      const res = await apiFetch(base);
      if (!res.ok) throw new Error(`List failed (${res.status})`);
      return readJson<DashboardTipAdmin[]>(res);
    },
    create: async (body: DashboardTipUpsert): Promise<DashboardTipAdmin> => {
      const res = await apiFetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { errorText?: string }).errorText ?? `Create failed (${res.status})`);
      }
      return readJson<DashboardTipAdmin>(res);
    },
    update: async (id: string, body: DashboardTipUpsert): Promise<DashboardTipAdmin> => {
      const res = await apiFetch(`${base}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { errorText?: string }).errorText ?? `Update failed (${res.status})`);
      }
      return readJson<DashboardTipAdmin>(res);
    },
    delete: async (id: string): Promise<void> => {
      const res = await apiFetch(`${base}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
    },
    downloadImportTemplate: async (): Promise<void> => {
      const res = await apiFetch(`${base}/import/template`);
      if (!res.ok) throw new Error(`Template download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dashboard-tips-template.json';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    importJson: async (file: File): Promise<DashboardTipImportResult> => {
      const form = new FormData();
      form.append('file', file);
      const res = await apiFetch(`${base}/import`, { method: 'POST', body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { errorText?: string }).errorText ?? `Import failed (${res.status})`);
      }
      return readJson<DashboardTipImportResult>(res);
    },
    uploadAsset: async (file: File): Promise<{ id: string }> => {
      const form = new FormData();
      form.append('file', file);
      const res = await apiFetch(`${base}/assets`, { method: 'POST', body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { errorText?: string }).errorText ?? `Image upload failed (${res.status})`);
      }
      return readJson<{ id: string }>(res);
    }
  };
}
